import React, { useEffect, useState } from "react";
import {
  Card as MuiCard,
  CardMedia,
  CardContent,
  Typography,
  Button,
  Box,
  TextField,
  Chip,
  IconButton,
  Fade,
  Skeleton,
} from "@mui/material";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  ShoppingCart as CartIcon,
  LocalOffer as OfferIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const CustomCard = ({
  image,
  title,
  description,
  mrp,
  ptr,
  offer,
  medicineId,
  loading = false,
}) => {
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [inCart, setInCart] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    const itemInCart = cart.find((item) => item.id === parseInt(medicineId));

    if (itemInCart) {
      setInCart(true);
      setQuantity(itemInCart.quantity);
    } else {
      setInCart(false);
      setQuantity(1);
    }
  }, [medicineId]);

  const handleCardClick = () => {
    navigate(`/product/${medicineId}`);
  };

  const handleAddToCart = () => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    const updatedCart = [...cart, { id: parseInt(medicineId), quantity }];
    localStorage.setItem("cart", JSON.stringify(updatedCart));
    setInCart(true);
  };

  const handleQuantityChange = (event) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value) && value >= 1 && value <= 999) {
      setQuantity(value);
      updateCart(value);
    }
  };

  const incrementQuantity = () => {
    if (quantity < 999) {
      const newQuantity = quantity + 1;
      setQuantity(newQuantity);
      updateCart(newQuantity);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 0) {
      const newQuantity = quantity - 1;
      if (newQuantity > 0) {
        setQuantity(newQuantity);
        updateCart(newQuantity);
      } else {
        removeFromCart();
        setInCart(false);
        setQuantity(1);
      }
    }
  };

  const updateCart = (newQuantity) => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    const updatedCart = cart.map((item) =>
      item.id === parseInt(medicineId)
        ? { ...item, quantity: newQuantity }
        : item
    );
    localStorage.setItem("cart", JSON.stringify(updatedCart));
  };

  const removeFromCart = () => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    const updatedCart = cart.filter((item) => item.id !== parseInt(medicineId));
    localStorage.setItem("cart", JSON.stringify(updatedCart));
  };

  // Calculate discount percentage
  const discountPercentage =
    mrp && ptr ? Math.round(((mrp - ptr) / mrp) * 100) : 0;

  if (loading) {
    return (
      <MuiCard
        sx={{
          maxWidth: 320,
          margin: "auto",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <Skeleton variant="rectangular" height={200} />
        <CardContent sx={{ flexGrow: 1 }}>
          <Skeleton variant="text" height={28} />
          <Skeleton variant="text" height={20} />
          <Skeleton variant="text" height={20} width="60%" />
        </CardContent>
        <Box sx={{ p: 2 }}>
          <Skeleton
            variant="rectangular"
            height={36}
            sx={{ borderRadius: 2 }}
          />
        </Box>
      </MuiCard>
    );
  }

  return (
    <MuiCard
      sx={{
        position: "relative",
        maxWidth: 320,
        margin: "auto",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 3,
        overflow: "hidden",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        cursor: "pointer",
        background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
        border: "1px solid",
        borderColor: isHovered ? "primary.main" : "grey.200",
        transform: isHovered ? "translateY(-8px)" : "translateY(0)",
        boxShadow: isHovered
          ? "0 20px 40px rgba(0, 128, 128, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)"
          : "0 4px 12px rgba(0, 0, 0, 0.05)",
        "&:hover": {
          "& .card-image": {
            transform: "scale(1.05)",
          },
        },
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Offer Badge */}
      {offer && (
        <Fade in={true}>
          <Chip
            icon={<OfferIcon sx={{ fontSize: 16 }} />}
            label={offer}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 2,
              background: "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)",
              color: "white",
              fontWeight: "bold",
              fontSize: "0.75rem",
              boxShadow: "0 4px 12px rgba(238, 90, 36, 0.3)",
              "& .MuiChip-icon": {
                color: "white",
              },
            }}
          />
        </Fade>
      )}

      {/* Discount Badge */}
      {discountPercentage > 0 && (
        <Chip
          label={`${discountPercentage}% OFF`}
          size="small"
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            zIndex: 2,
            background: "linear-gradient(135deg, #00b894 0%, #00a085 100%)",
            color: "white",
            fontWeight: "bold",
            fontSize: "0.7rem",
            boxShadow: "0 2px 8px rgba(0, 184, 148, 0.3)",
          }}
        />
      )}

      {/* Product Image */}
      <Box sx={{ position: "relative", overflow: "hidden" }}>
        {!imageLoaded && (
          <Skeleton
            variant="rectangular"
            height={200}
            sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
          />
        )}
        <CardMedia
          component="img"
          height="200"
          image={image}
          alt={title}
          className="card-image"
          onLoad={() => setImageLoaded(true)}
          onClick={handleCardClick}
          sx={{
            objectFit: "contain",
            padding: 2,
            transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            display: imageLoaded ? "block" : "none",
          }}
        />
      </Box>

      {/* Card Content */}
      <CardContent
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          p: 3,
          pb: 1,
        }}
      >
        <Typography
          variant="h6"
          component="h3"
          onClick={handleCardClick}
          sx={{
            fontWeight: 600,
            fontSize: "1rem",
            lineHeight: 1.3,
            mb: 1,
            color: "text.primary",
            cursor: "pointer",
            transition: "color 0.2s ease",
            "&:hover": {
              color: "primary.main",
            },
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 2,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            lineHeight: 1.4,
          }}
        >
          {description}
        </Typography>

        {/* Pricing Section */}
        <Box sx={{ mt: "auto" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography
              variant="h6"
              component="span"
              sx={{
                fontWeight: 700,
                color: "primary.main",
                fontSize: "1.1rem",
              }}
            >
              ₹{ptr}
            </Typography>
            {mrp !== ptr && (
              <Typography
                variant="body2"
                component="span"
                sx={{
                  textDecoration: "line-through",
                  color: "text.secondary",
                  fontSize: "0.9rem",
                }}
              >
                ₹{mrp}
              </Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            MRP: ₹{mrp} | PTR: ₹{ptr}
          </Typography>
        </Box>
      </CardContent>

      {/* Action Section */}
      <Box sx={{ p: 3, pt: 1 }}>
        {!inCart ? (
          <Button
            fullWidth
            variant="contained"
            startIcon={<CartIcon />}
            onClick={handleAddToCart}
            sx={{
              background: "linear-gradient(135deg, #008080 0%, #006666 100%)",
              borderRadius: 2,
              py: 1.5,
              fontWeight: 600,
              textTransform: "none",
              fontSize: "0.95rem",
              boxShadow: "0 4px 12px rgba(0, 128, 128, 0.3)",
              transition: "all 0.2s ease",
              "&:hover": {
                background: "linear-gradient(135deg, #006666 0%, #004d4d 100%)",
                transform: "translateY(-1px)",
                boxShadow: "0 6px 16px rgba(0, 128, 128, 0.4)",
              },
            }}
          >
            Add to Cart
          </Button>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              p: 1,
              borderRadius: 2,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "grey.200",
            }}
          >
            <IconButton
              size="small"
              onClick={decrementQuantity}
              disabled={quantity <= 0}
              sx={{
                bgcolor: "primary.main",
                color: "white",
                width: 32,
                height: 32,
                "&:hover": {
                  bgcolor: "primary.dark",
                },
                "&:disabled": {
                  bgcolor: "grey.300",
                },
              }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>

            <TextField
              size="small"
              value={quantity}
              onChange={handleQuantityChange}
              inputProps={{
                min: 1,
                max: 999,
                style: {
                  textAlign: "center",
                  fontWeight: 600,
                },
              }}
              sx={{
                width: 60,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 1,
                  "& fieldset": {
                    borderColor: "grey.300",
                  },
                },
              }}
            />

            <IconButton
              size="small"
              onClick={incrementQuantity}
              disabled={quantity >= 999}
              sx={{
                bgcolor: "primary.main",
                color: "white",
                width: 32,
                height: 32,
                "&:hover": {
                  bgcolor: "primary.dark",
                },
                "&:disabled": {
                  bgcolor: "grey.300",
                },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
    </MuiCard>
  );
};

export default CustomCard;
